/* Copyright (c) 2024, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { normalizePassiveListenerOptions, _getEventTarget } from '@angular/cdk/platform';
import { NgZone } from '@angular/core';

/** Options used to bind a passive capturing event. */
const passiveCapturingEventOptions = normalizePassiveListenerOptions({
    passive: true,
    capture: true
});

/** Manages events through delegation so that as few event handlers as possible are bound. */
export class EventManager {
    private _events = new Map<string, Map<HTMLElement, Set<EventListenerObject>>>();

    /** Adds an event handler. */
    addHandler(ngZone: NgZone, name: string, element: HTMLElement, handler: EventListenerObject) {
        const handlersForEvent = this._events.get(name);

        if (handlersForEvent) {
            const handlersForElement = handlersForEvent.get(element);

            if (handlersForElement) {
                handlersForElement.add(handler);
            } else {
                handlersForEvent.set(element, new Set([handler]));
            }
        } else {
            this._events.set(name, new Map([[element, new Set([handler])]]));

            ngZone.runOutsideAngular(() => {
                document.addEventListener(name, this._delegateEventHandler, passiveCapturingEventOptions);
            });
        }
    }

    /** Removes an event handler. */
    removeHandler(name: string, element: HTMLElement, handler: EventListenerObject) {
        const handlersForEvent = this._events.get(name);

        if (!handlersForEvent) {
            return;
        }

        const handlersForElement = handlersForEvent.get(element);

        if (!handlersForElement) {
            return;
        }

        handlersForElement.delete(handler);

        if (handlersForElement.size === 0) {
            handlersForEvent.delete(element);
        }

        if (handlersForEvent.size === 0) {
            this._events.delete(name);
            document.removeEventListener(name, this._delegateEventHandler, passiveCapturingEventOptions);
        }
    }

    /** Event handler that is bound and which dispatches the events to the different targets. */
    private _delegateEventHandler = (event: Event) => {
        const target = _getEventTarget(event);

        if (!target) {
            return;
        }

        // Caveat: invokes handlers according to insertion order not DOM order
        // so it is likely that a parent element would handle an event earlier than its child
        // even it doesn't set capture to true
        // Also, there is no way to stop invoking the handlers if one of the handlers calls
        // stopPropagation or stopImmediatePropagation to prevent its parent or listeners
        // at the same level to receive the event
        this._events.get(event.type)?.forEach((handlers, element) => {
            if (element === target || element.contains(target as Node)) {
                handlers.forEach(handler => handler.handleEvent(event));
            }
        });
    };
}
